// Global Instructions Rule Applied!
import { useEffect, useState } from 'react'

/**
 * Mobile breakpoint. Viewport widths below 768px (max-width 767px) render the mobile
 * shell; 768px and up keep the desktop shell. Mirrors Ant Design's `md` breakpoint so
 * the two shells switch at the same point antd's responsive props do.
 */
export const MOBILE_MEDIA_QUERY = '(max-width: 767px)'

function getMatches(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia(MOBILE_MEDIA_QUERY).matches
}

/**
 * Returns true when the viewport is phone-sized.
 *
 * The initial value is read synchronously from `matchMedia` so the correct shell paints
 * on the very first render — important for the installed PWA cold launch, which would
 * otherwise flash the desktop shell before the effect ran. The value then stays in sync
 * with viewport changes (resize / device rotation) via a `change` listener.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(getMatches)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }
    const mediaQueryList = window.matchMedia(MOBILE_MEDIA_QUERY)
    const handleChange = (event: MediaQueryListEvent) => setIsMobile(event.matches)
    mediaQueryList.addEventListener('change', handleChange)
    // Re-sync in case the viewport changed between first render and effect attachment.
    setIsMobile(mediaQueryList.matches)
    return () => mediaQueryList.removeEventListener('change', handleChange)
  }, [])

  return isMobile
}

export default useIsMobile
